import { Button, Space, Tag, theme, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import clsx from 'clsx';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';
import { Routes } from '../../../../app/routes';
import ColumnsSelectorModal from '../../../../shared/components/columns-selector/columns-selector';
import { ExpandTableParentIcon } from '../../../../shared/components/expand-table-parent-icon/expand-table-parent-icon';
import IconWrapper from '../../../../shared/components/icon-wrapper/icon-wrapper';
import Table from '../../../../shared/components/table/table';
import { ClockHistoryIcon } from '../../../../shared/icons/clock-history-icon';
import { DocumentAddIcon } from '../../../../shared/icons/document-add-icon';
import { DocumentImagePlusIcon } from '../../../../shared/icons/document-image-plus-icon';
import { DocumentRelatedAddIcon } from '../../../../shared/icons/document-related-add-icon';
import { useAppSelector } from '../../../../store/hooks';
import styles from '../../../../styles/components/scan-sign-requests-table.module.css';
import { fileUploadModalId } from '../../../archive/constants/constants';
import { selectActiveRequestId, setActiveRequestId } from '../../../request/store/requests-slice';
import { scanRequestFormModalId } from '../../../scan-requests/constants/constants';
import { TemplateField } from '../../../templates/models/template-field';
import { openModal } from '../../../ui/store/ui-slice';
import { useGetUserQuery, useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { UserSettings } from '../../../user/models/user-settings';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { signRequestCreateModalId } from '../../constants/constants';
import { SignRequestStatus } from '../../models/sign-request-status';
import { SignRequestTableModel } from '../../models/sign-requests-table-model';
import {
  selectActiveSignRequestId,
  selectIsSignRequestPreviewOpen,
  selectSelectedSignRequestIds,
  setActiveSignRequestId,
  setIsSignRequestPreviewOpen,
  setSelectedSignRequestIds,
  setTempSignRequestId,
} from '../../store/sign-requests-slice';

interface Props {
  isFetchingSignRequests: boolean;
  signRequestsDataSource: SignRequestTableModel[];
  templateFields?: TemplateField[];
}
const { useToken } = theme;

const SignRequestsTable = ({ isFetchingSignRequests, signRequestsDataSource, templateFields }: Props): JSX.Element => {
  const { t } = useTranslation(['common', 'sign-requests']);
  const activeSignRequestId = useAppSelector(selectActiveSignRequestId);
  const activeRequestId = useAppSelector(selectActiveRequestId);
  const isSignRequestPreviewOpen = useAppSelector(selectIsSignRequestPreviewOpen);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { data: userSettings } = useGetUserSettingsQuery();
  const { data: currentUser } = useGetUserQuery();
  const [searchParams] = useSearchParams();
  const selectedUnarchivedRequests = useAppSelector(selectSelectedSignRequestIds);
  const { token } = useToken();
  // it is needed to use useMediaQuery because antd breakpoint hook returns real token values.
  const matchesScreenXXL = useMediaQuery(`(min-width: ${token.screenXXL}px)`);

  const columns: ColumnsType<SignRequestTableModel> = useMemo(
    () => [
      {
        dataIndex: 'expand',
        key: 'expand',
        className: 'text-nowrap',
        width: 70,
      },
      {
        title: userSettings?.[UserSettings.SignRequestNameTitle] || t('label.name'),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t('label.appUser', { ns: 'sign-requests' }),
        dataIndex: 'user',
        key: 'user',
      },
      {
        title: t('label.customer'),
        dataIndex: 'customer',
        key: 'customer',
        className: 'text-break-normal',
        hidden: !(currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers)),
      },
      {
        title: t('label.unit'),
        dataIndex: 'unit',
        key: 'unit',
        className: 'text-break-normal',
      },
      {
        title: t('label.status'),
        dataIndex: 'status',
        key: 'status',
        className: 'text-break-normal',

        render: (value, record) => (
          <Tag className="text-wrap-auto" color={record.status === SignRequestStatus.Signed ? 'success' : 'default'}>
            {record.status === SignRequestStatus.Signed
              ? t('statusOption.signed', { ns: 'sign-requests' })
              : record.status === SignRequestStatus.Pending
                ? t('signScanRequest.label.statusOption.pending')
                : ''}
          </Tag>
        ),
      },
      {
        title: t('signScanRequest.label.signedAt'),
        dataIndex: 'signedAt',
        key: 'signedAt',
        className: 'text-break-normal',
      },
      {
        title: t('label.template'),
        dataIndex: 'template',
        key: 'template',
        className: 'text-break-normal',
      },
      {
        title: t('signRequestsTable.appVersion', { ns: 'sign-requests' }),
        dataIndex: 'appVersion',
        key: 'appVersion',
        className: 'text-break-normal',
      },
      {
        title: t('signScanRequest.label.uploadToServerStatus'),
        dataIndex: 'archived',
        key: 'archived',
        className: 'text-break-normal',
        render: (value, record) =>
          record.canArchive ? (
            <Tag className="text-wrap-auto" color={record.archived ? 'success' : 'default'}>
              {record.archived ? t('signScanRequest.label.uploaded') : t('signScanRequest.label.uploadFailed')}
            </Tag>
          ) : (
            <Tag color="default">N/A</Tag>
          ),
      },
      {
        title: t('signScanRequest.label.relatedSignRequestCount'),
        dataIndex: 'signRequestCount',
        key: 'signRequestCount',
        className: 'text-break-normal',
      },
      {
        title: t('signScanRequest.label.relatedScanRequestCount'),
        dataIndex: 'scanRequestCount',
        key: 'scanRequestCount',
        className: 'text-break-normal',
      },
      {
        title: t('signRequestsTable.relatedFileCount', { ns: 'sign-requests' }),
        dataIndex: 'relatedFilesCount',
        key: 'relatedFilesCount',
        className: 'text-break-normal',
      },
      ...(templateFields || []).map(templateField => ({
        title: templateField.label,
        dataIndex: templateField.label,
        key: templateField.name,
      })),
      {
        title: t('label.actions'),
        key: 'actions',
        fixed: 'right',
        width: 224,
        render: record => (
          <Space size="middle">
            <Tooltip mouseEnterDelay={0.5} title={t('signScanRequest.action.scanDocument')}>
              <Button
                onClick={event => {
                  event.stopPropagation();
                  dispatch(openModal({ id: scanRequestFormModalId, props: { requestId: record.request.id } }));
                }}
                type="text"
                shape="circle"
                disabled={record.isDeleted}
                icon={
                  <IconWrapper
                    textProps={{
                      type: 'success',
                    }}
                  >
                    <DocumentAddIcon />
                  </IconWrapper>
                }
              />
            </Tooltip>
            <Tooltip mouseEnterDelay={0.5} title={t('action.history')}>
              <Link to={Routes.SignRequestTraceLogs.replace(':id', record.key.split('_')[0])}>
                <Button
                  onClick={event => event.stopPropagation()}
                  type="text"
                  shape="circle"
                  icon={<ClockHistoryIcon />}
                />
              </Link>
            </Tooltip>
            <Tooltip mouseEnterDelay={0.5} title={t('signScanRequest.action.additionalDocument')}>
              <Button
                onClick={event => {
                  event.stopPropagation();
                  dispatch(
                    openModal({
                      id: signRequestCreateModalId,
                      props: {
                        signRequestId: record.id,
                        requestId: record.request.id,
                        isStatusSigned: record.status === SignRequestStatus.Signed,
                      },
                    }),
                  );
                }}
                type="text"
                shape="circle"
                disabled={record.isDeleted}
                icon={
                  <IconWrapper>
                    <DocumentImagePlusIcon />
                  </IconWrapper>
                }
              />
            </Tooltip>
            <Tooltip mouseEnterDelay={0.5} title={t('signRequestsTable.relatedDocument', { ns: 'sign-requests' })}>
              <Button
                onClick={event => {
                  event.stopPropagation();
                  dispatch(
                    openModal({
                      id: fileUploadModalId,
                      props: {
                        requestId: record.request.id,
                      },
                    }),
                  );
                }}
                type="text"
                shape="circle"
                disabled={record.isDeleted}
                icon={
                  <IconWrapper>
                    <DocumentRelatedAddIcon />
                  </IconWrapper>
                }
              />
            </Tooltip>
          </Space>
        ),
      },
    ],

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, templateFields, currentUser],
  );

  const visibleColumns = useMemo(() => {
    const settingName = UserSettings.SignRequestsTableColumns + (searchParams.get('template') || '');

    const signRequestsTableColumnsKeyArray =
      (userSettings?.[settingName] && userSettings?.[settingName].split(',')) || [];

    const selectedColumns = columns
      .filter(column => column.key && signRequestsTableColumnsKeyArray.includes(column.key as string))
      .sort(
        (a, b) =>
          signRequestsTableColumnsKeyArray.indexOf(a.key as string) -
          signRequestsTableColumnsKeyArray.indexOf(b.key as string),
      );

    return selectedColumns.length ? selectedColumns : columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings, templateFields]);

  useEffect(() => {
    if (!matchesScreenXXL && activeRequestId && activeSignRequestId && isSignRequestPreviewOpen) {
      navigate(
        Routes.SignRequest.replace(':signRequestId', activeSignRequestId).replace(':requestId', activeRequestId),
        { state: { from: location } },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRequestId, matchesScreenXXL, activeSignRequestId, isSignRequestPreviewOpen]);

  const handleOnSignRequestRowClick = (record: SignRequestTableModel, shouldTogglePreview = false) => {
    dispatch(setTempSignRequestId(null));

    if (!record.children) {
      dispatch(setTempSignRequestId(record.id));
      dispatch(setIsSignRequestPreviewOpen(true));
      return;
    }

    if (activeSignRequestId === record.id) {
      if (!isSignRequestPreviewOpen && shouldTogglePreview) {
        dispatch(setIsSignRequestPreviewOpen(true));
        return;
      }

      dispatch(setActiveRequestId(null));
      dispatch(setActiveSignRequestId(null));
      dispatch(setTempSignRequestId(null));
      dispatch(setIsSignRequestPreviewOpen(false));
      return;
    }

    dispatch(setActiveRequestId(record.request.id));
    dispatch(setActiveSignRequestId(record.id));

    if (shouldTogglePreview) {
      dispatch(setIsSignRequestPreviewOpen(true));
    }
  };

  return (
    <>
      <ColumnsSelectorModal columns={columns} visibleColumns={visibleColumns} />
      <div className={styles.tableContainer}>
        <Table
          tableProps={{
            rowClassName: record =>
              clsx({
                [styles.requestIsDeleted]: record.isDeleted,
                [styles.clickableRow]: true,
                [styles.activeRow]: record.id === activeSignRequestId,
              }),
            scroll: {
              ...(visibleColumns.length > 9 && {
                x: Math.max(visibleColumns.length * 150, 2000) - (activeSignRequestId ? 500 : 0),
              }),
              y: 'calc(100vh - 160px)',
            },
            columns: visibleColumns,
            dataSource: signRequestsDataSource,
            onRow: (record: SignRequestTableModel) => ({
              onClick: () => handleOnSignRequestRowClick(record, true),
            }),
            loading: isFetchingSignRequests,
            rowSelection: {
              type: 'checkbox',
              selectedRowKeys: selectedUnarchivedRequests || [],
              onSelect: () => {
                const transformedSelectedRows = selectedUnarchivedRequests.map(requestId => requestId);
                dispatch(setSelectedSignRequestIds(transformedSelectedRows));
              },
              onChange: selectedRowKeys => {
                return dispatch(setSelectedSignRequestIds(selectedRowKeys as string[]));
              },
              preserveSelectedRowKeys: true,
            },
            expandable: {
              indentSize: 0,
              expandedRowKeys: activeSignRequestId ? [activeSignRequestId] : [],
              expandIcon: ({ expanded, record }) =>
                record.children && record.signRequestCount > 1 ? (
                  <ExpandTableParentIcon
                    expanded={expanded}
                    onClick={event => {
                      event.stopPropagation();
                      event.preventDefault();
                      handleOnSignRequestRowClick(record);
                    }}
                  />
                ) : undefined,
            },
            pagination: false,
          }}
        />
      </div>
    </>
  );
};

export default SignRequestsTable;
